/**
 * Scope Store · 组织与权限（领域层，无 UI）
 * ------------------------------------------------------------
 * 原型对应：Portal / applyOrgChrome / setOrg
 * 核心：当前组织是谁、可见范围是什么、权限边界在哪。
 * 切组织 → orgId（mock 模块内）变更 → emit('org:changed') → 全站重绘。
 */
import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'
import { authUser } from '@/data/api/auth-store'
import { orgRepo, rememberOrgSelection } from '@/data/api/org-repository'
import { emit } from '@/shared/events'
import type { ExecPerm } from './types'

export const useScopeStore = defineStore('scope', () => {
  const orgs = ref<Record<string, any>[]>([])
  const current = ref<Record<string, any> | null>(null)

  const busy=ref(false),error=ref('')
  let generation=0, pending=''
  async function init() {
    const ticket=++generation;busy.value=true;error.value='';current.value=null;orgs.value=[]
    try {
      const rows=await orgRepo.list();if(ticket!==generation)return
      orgs.value=rows
      const id=rows.find(o=>o.id===orgRepo.id())?.id||rows[0]?.id
      if(!id){rememberOrgSelection('');return}
      const chosen=await orgRepo.set(id);if(ticket!==generation)return
      current.value=chosen;rememberOrgSelection(chosen.id);emit('org:changed',chosen)
    } catch(e){if(ticket===generation){current.value=null;orgs.value=[];error.value=(e as Error).message}}
    finally{if(ticket===generation)busy.value=false}
  }
  async function switchOrg(id:string) {
    if(id===pending||id===current.value?.id)return
    const ticket=++generation;pending=id;busy.value=true;error.value='';current.value=null
    try{const chosen=await orgRepo.set(id);if(ticket!==generation)return;current.value=chosen;rememberOrgSelection(chosen.id);emit('org:changed',chosen)}
    catch(e){if(ticket===generation){current.value=null;error.value=(e as Error).message;rememberOrgSelection('')}}
    finally{if(ticket===generation){busy.value=false;pending=''}}
  }
  watch(()=>authUser.value?.personId,()=>{generation++;pending='';current.value=null;orgs.value=[];busy.value=false;error.value='';if(authUser.value)void init()})

  const isFresh = computed(() => !!current.value?.fresh)
  const permLabel = computed(() => {
    const p = String(current.value?.perm ?? '')
    if (p === '受控执行 · 需确认' || p.includes('受控')) return '受控执行'
    if (p.includes('仅建议')) return '仅建议'
    if (p.includes('仅浏览')) return '仅浏览'
    return p || '—'
  })
  const permLevel = computed<ExecPerm>(() => {
    const p = String(current.value?.perm ?? '')
    if (current.value?.exec) return p.includes('受控') ? 'controlled' : 'full'
    return 'readonly'
  })
  const domainCount = computed(() => (current.value?.domains ?? []).length)

  return {
    orgs, current, init, switchOrg, busy, error,
    isFresh, permLabel, permLevel, domainCount
  }
})
